import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { GameCard, GameCardSkeleton } from '../components/GameCard';
import HeroVisual from '../components/HeroVisual';
import { useStore } from '../context/StoreContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search } from 'lucide-react';
import theme from '../theme.config';

const HERO_BG = theme.assets.heroBg;
const COPY = theme.copy;

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'all', name: 'Semua' },
  { id: 'games', name: 'Games' },
  { id: 'pulsa', name: 'Pulsa' },
  { id: 'voucher', name: 'Voucher' },
];

const TICKER_LOGS = [
  '✦ VOLT · 2500 Diamond dikirim → MLBB UID 8291',
  '✦ SURGE · 86 UC energi → FreeFire Grid-7',
  '✦ PERSEMBAHAN · Voucher 50K terkirim ke gerbang',
  '✦ VOLT · Genshin Impact 980 Genesis → UID 712',
  '✦ SURGE · QRIS selesai — TXN VGX-9AF2',
  '✦ PERSEMBAHAN · Pulsa 25K → 08123456789 terkirim',
  '✦ VOLT · Call of Duty 400 CP terproses',
  '✦ SURGE · Reseller node disinkronkan — tier BRAHMANA',
  '✦ PERSEMBAHAN · Valorant 1650 VP → ID 81920843',
  '✦ VOLT · QRIS token dibuat — TXN VGX-0x9AF2',
];

const FEATURE_ICONS = ['⚡', '🏛', '🛡', '✦'];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [digiflazzBrands, setDigiflazzBrands] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { filterProducts(); }, [products, digiflazzBrands, activeCategory, searchQuery]); // eslint-disable-line

  const fetchAll = async () => {
    try {
      const [seedRes, catalogRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/biller/catalog`),
      ]);
      if (seedRes.status === 'fulfilled') setProducts(seedRes.value.data.products || []);
      if (catalogRes.status === 'fulfilled' && catalogRes.value.data.success) {
        setDigiflazzBrands(catalogRes.value.data.brands || []);
      }
      try { await axios.post(`${API_URL}/api/seed`); } catch {}
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProducts = () => {
    const allItems = [];
    for (const brand of digiflazzBrands) {
      allItems.push({
        id: `df-${brand.slug}`,
        slug: `df/${brand.slug}`,
        name: brand.brand,
        image: brand.image,
        category: brand.category,
        source: 'digiflazz',
        itemCount: brand.items.length,
        startPrice: Math.min(...brand.items.map(i => i.price)),
      });
    }
    const dfNames = new Set(digiflazzBrands.map(b => b.brand.toLowerCase()));
    for (const p of products) {
      if (!dfNames.has(p.name.toLowerCase())) {
        const catMap = { game: 'games', voucher: 'voucher' };
        allItems.push({ ...p, category: catMap[p.category] || p.category, source: 'seed' });
      }
    }
    let filtered = [...allItems];
    if (activeCategory !== 'all') filtered = filtered.filter(p => p.category === activeCategory);
    if (searchQuery) filtered = filtered.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
    setFilteredProducts(filtered);
  };

  return (
    <div className="min-h-screen pt-16 stone-texture">

      {/* ══ HERO: Sacred Temple Gateway ══ */}
      <section className="relative overflow-hidden" style={{ minHeight: '92vh' }}>
        {/* Background image — full bleed */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${HERO_BG}')` }}
        />
        {/* Stone to void overlay */}
        <div className="hero-overlay absolute inset-0" />
        {/* Subtle mandala pattern overlay */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'repeating-conic-gradient(from 0deg, hsl(var(--primary) / 0.025) 0deg 10deg, transparent 10deg 20deg)',
            backgroundSize: '80px 80px',
          }}
        />
        {/* Ambient gold glow at bottom */}
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[120px] pointer-events-none"
          style={{ background: 'hsl(var(--primary) / 0.06)' }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* ── LEFT: Headline + Search ── */}
            <div className="animate-fade-up">
              {/* Sacred badge */}
              <div
                className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-sm"
                style={{
                  border: '1px solid hsl(var(--primary) / 0.35)',
                  background: 'hsl(var(--primary) / 0.06)',
                }}
              >
                <span className="text-primary animate-torch text-xs">✦</span>
                <span
                  className="text-xs text-primary tracking-widest uppercase"
                  style={{ fontFamily: "'Space Mono', monospace" }}
                >
                  Warisan Digital Nusantara
                </span>
                <span className="text-primary animate-torch text-xs" style={{ animationDelay: '0.5s' }}>✦</span>
              </div>

              {/* Main heading */}
              <h1
                className="font-bold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-2 leading-tight"
                style={{ fontFamily: "'Cinzel Decorative', serif" }}
              >
                {COPY.hero.titleLine1}
              </h1>
              <h2
                className="font-bold text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mb-8 leading-tight relief-text"
                style={{ fontFamily: "'Cinzel Decorative', serif" }}
              >
                {COPY.hero.titleLine2}
              </h2>

              <p
                className="text-foreground/70 text-base md:text-lg mb-10 max-w-xl leading-relaxed"
                style={{ fontFamily: "'Lora', serif" }}
              >
                {COPY.hero.subtitle}
              </p>

              {/* Search — stone style */}
              <div className="relative max-w-lg mb-10">
                <Search
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4"
                  style={{ color: 'hsl(var(--primary) / 0.6)' }}
                />
                <Input
                  type="text"
                  placeholder={COPY.hero.searchPlaceholder}
                  className="pl-11 pr-4 py-6 text-white placeholder:text-muted-foreground/60 text-sm focus:ring-1 rounded-sm"
                  style={{
                    background: 'hsl(var(--background) / 0.75)',
                    border: '1px solid hsl(var(--primary) / 0.3)',
                    fontFamily: "'Lora', serif",
                    backdropFilter: 'blur(12px)',
                    outline: 'none',
                  }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                  onFocus={(e) => { e.target.style.borderColor = 'hsl(var(--primary) / 0.7)'; e.target.style.boxShadow = '0 0 0 2px hsl(var(--primary) / 0.12), 0 0 16px hsl(var(--primary) / 0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'hsl(var(--primary) / 0.3)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>

              {/* Quick stats */}
              <div className="flex items-center gap-8">
                {[
                  { label: 'Produk', val: loading ? '…' : filteredProducts.length.toString() },
                  { label: 'Instan', val: '< 3s' },
                  { label: 'Uptime', val: '99.9%' },
                ].map(s => (
                  <div key={s.label}>
                    <p
                      className="text-2xl font-bold text-glow-gold"
                      style={{ color: 'hsl(var(--primary))', fontFamily: "'Cinzel Decorative', serif" }}
                    >
                      {s.val}
                    </p>
                    <p
                      className="text-[10px] text-muted-foreground uppercase tracking-widest mt-0.5"
                      style={{ fontFamily: "'Space Mono', monospace" }}
                    >
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── RIGHT: Mandala Visual ── */}
            <div className="hidden lg:flex items-center justify-center">
              <div
                className="rounded-full p-8 stone-card"
                style={{
                  background: 'hsl(var(--background) / 0.6)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid hsl(var(--primary) / 0.15)',
                  boxShadow: '0 0 60px hsl(var(--primary) / 0.05), inset 0 0 40px hsl(var(--background) / 0.5)',
                }}
              >
                <HeroVisual />
              </div>
            </div>
          </div>
        </div>

        {/* Live ticker at bottom of hero */}
        <div
          className="absolute bottom-0 left-0 right-0 py-3 overflow-hidden"
          style={{
            background: 'hsl(var(--background) / 0.85)',
            borderTop: '1px solid hsl(var(--primary) / 0.15)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <div className="flex whitespace-nowrap animate-ticker">
            {[...TICKER_LOGS, ...TICKER_LOGS].map((log, i) => (
              <span
                key={i}
                className="px-8 text-xs"
                style={{
                  fontFamily: "'Space Mono', monospace",
                  color: i % 3 === 0
                    ? 'hsl(var(--primary) / 0.8)'
                    : i % 3 === 1
                    ? 'hsl(var(--secondary) / 0.7)'
                    : 'hsl(var(--foreground) / 0.4)',
                }}
              >
                {log}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FEATURES BAR ══ */}
      <section className="py-12 border-b" style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--card) / 0.4)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {COPY.features.map((f, idx) => (
              <div key={idx} className="flex items-center gap-4 group">
                <div
                  className="w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-105"
                  style={{
                    background: 'hsl(var(--primary) / 0.08)',
                    border: '1px solid hsl(var(--primary) / 0.25)',
                  }}
                >
                  <span className="text-xl">{FEATURE_ICONS[idx % FEATURE_ICONS.length]}</span>
                </div>
                <div>
                  <h3
                    className="font-bold text-white text-sm"
                    style={{ fontFamily: "'Cinzel', serif" }}
                  >
                    {f.label}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: "'Lora', serif" }}>
                    {f.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PRODUCTS GRID ══ */}
      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Section heading */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-px flex-1" style={{ background: 'linear-gradient(to right, transparent, hsl(var(--primary) / 0.3))' }} />
              <span className="text-primary/50 text-sm">✦</span>
            </div>
            <h2
              className="text-2xl md:text-3xl font-bold relief-text uppercase tracking-wide"
              style={{ fontFamily: "'Cinzel Decorative', serif" }}
            >
              Pilih Persembahan Digital
            </h2>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                className="px-5 py-2 rounded-sm text-sm uppercase tracking-widest border transition-all duration-200"
                style={{
                  fontFamily: "'Cinzel', serif",
                  fontSize: '11px',
                  background: activeCategory === cat.id ? 'hsl(var(--primary) / 0.12)' : 'transparent',
                  borderColor: activeCategory === cat.id ? 'hsl(var(--primary) / 0.7)' : 'hsl(var(--border))',
                  color: activeCategory === cat.id ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))',
                  boxShadow: activeCategory === cat.id ? '0 0 10px hsl(var(--primary) / 0.15)' : 'none',
                }}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`category-${cat.id}`}
              >
                {activeCategory === cat.id ? '✦ ' : ''}{cat.name}
              </button>
            ))}
            {!loading && (
              <span
                className="ml-auto text-xs text-muted-foreground"
                style={{ fontFamily: "'Space Mono', monospace" }}
              >
                {filteredProducts.length} PRODUK DITEMUKAN
              </span>
            )}
          </div>

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {[...Array(10)].map((_, i) => <GameCardSkeleton key={i} />)}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {filteredProducts.map((product) => (
                <GameCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-muted-foreground" style={{ fontFamily: "'Lora', serif" }}>
                ✦ Tidak ada produk ditemukan di gerbang ini
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ══ CTA SECTION ══ */}
      <section className="py-20 border-t" style={{ borderColor: 'hsl(var(--border))' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="relative overflow-hidden rounded-sm p-10 md:p-16"
            style={{
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--primary) / 0.2)',
              boxShadow: '0 0 80px hsl(var(--primary) / 0.04)',
            }}
          >
            {/* Background mandala watermark */}
            <div
              className="absolute right-8 top-1/2 -translate-y-1/2 w-64 h-64 rounded-full opacity-5 pointer-events-none"
              style={{
                background: 'radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)',
              }}
            />
            <div
              className="absolute right-8 top-1/2 -translate-y-1/2 w-48 h-48 rounded-full opacity-5 pointer-events-none animate-mandala-slow"
              style={{ border: '1px solid hsl(var(--primary))', margin: '8px' }}
            />
            {/* Gold top border */}
            <div
              className="absolute top-0 left-0 right-0 h-px"
              style={{ background: 'linear-gradient(to right, transparent, hsl(var(--primary) / 0.5), transparent)' }}
            />
            {/* Gold bottom border */}
            <div
              className="absolute bottom-0 left-0 right-0 h-px"
              style={{ background: 'linear-gradient(to right, transparent, hsl(var(--secondary) / 0.4), transparent)' }}
            />

            <div className="relative z-10 flex flex-col md:flex-row items-start justify-between gap-8">
              <div className="max-w-xl">
                <div
                  className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-sm"
                  style={{ border: '1px solid hsl(var(--secondary) / 0.35)', background: 'hsl(var(--secondary) / 0.06)' }}
                >
                  <span className="text-secondary text-xs animate-torch">✦</span>
                  <span
                    className="text-[10px] text-secondary tracking-widest uppercase"
                    style={{ fontFamily: "'Space Mono', monospace" }}
                  >
                    PROGRAM BRAHMANA
                  </span>
                </div>
                <h2
                  className="font-bold text-2xl md:text-3xl text-white uppercase mb-4 leading-tight"
                  style={{ fontFamily: "'Cinzel Decorative', serif" }}
                >
                  {COPY.cta.titlePrefix}
                  <span className="relief-text">{COPY.cta.titleHighlight}</span>
                  {COPY.cta.titleSuffix}
                </h2>
                <p className="text-foreground/65 mb-7 leading-relaxed" style={{ fontFamily: "'Lora', serif" }}>
                  {COPY.cta.subtitle}
                </p>
                <Button
                  className="stone-btn font-bold uppercase tracking-widest py-6 px-8 text-background text-sm"
                  style={{
                    fontFamily: "'Cinzel', serif",
                    background: 'linear-gradient(135deg, #C9A227, #A07B1E)',
                    border: 'none',
                    letterSpacing: '0.08em',
                  }}
                  onClick={() => window.location.href = '/reseller'}
                  data-testid="cta-reseller-btn"
                >
                  ✦ {COPY.cta.button}
                </Button>
              </div>

              {/* Mini mandala visual */}
              <div className="hidden md:block flex-shrink-0">
                <HeroVisual compact />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
